import React, { useEffect, useRef, useState } from 'react';
import './index.css';
import OutletHead from '../../../../../components/common/outlet-head';
import {
  Button,
  OverlayTrigger,
  Spinner,
  Tooltip,
} from 'react-bootstrap';
import {
  IoArrowBackOutline,
  IoCloudDownload,
  IoDocumentTextOutline,
  IoEye,
  IoReceiptOutline,
  IoSend,
  IoSettingsOutline,
  IoShareSocial,
} from 'react-icons/io5';
import { BsPrinter } from 'react-icons/bs';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import generatePDF from 'react-to-pdf';
import { render } from '@react-email/components';
import { useAuth } from '../../../../../context/AuthContext';
import InvoiceTemplate from '../../../../../components/invoices/basic';
import { useReactToPrint } from 'react-to-print';
import { db } from '../../../../../firebase';
import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { toast } from 'react-toastify';
import Skeleton from 'react-loading-skeleton';
import jsPDF from 'jspdf';

const safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const formatINR = (n) =>
  safeNum(n).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

function InvoiceDetails() {
  const targetInvoiceRef = useRef();

  const { user, businessProfile } = useAuth();
  const { bid } = user.claims;
  const location = useLocation();
  const { state } = location;
  const { bookingData, invoiceID } = state;
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [data, setData] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [totalPaidAmount, setTotalPaidAmount] = useState(0);
  const [overdueSummary, setOverdueSummary] = useState({
    overdueTotal: 0,
    overdueReceived: 0,
    overdueOutstanding: 0,
  });

  // ---------- RECEIPTS FOR THIS INVOICE ----------
  function getReceipts(bookingID, invoiceID) {
    const qRept = query(
      collection(db, 'Businesses', bid, 'Receipts'),
      where('bookingID', '==', bookingID)
    );
    const unsubscribe = onSnapshot(
      qRept,
      (querySnapshot) => {
        const tempData = [];
        querySnapshot.forEach((docSnap) => {
          const isInvoice = (docSnap.data().invoiceIDs || []).find(
            (x) => x.id === invoiceID
          );
          if (isInvoice) {
            tempData.push({ id: docSnap.id, ...docSnap.data() });
          }
        });
        setReceipts(tempData);
      },
      (error) => {
        console.log(error.message);
      }
    );

    return () => unsubscribe();
  }

  // ---------- MAIN INVOICE SNAPSHOT ----------
  useEffect(() => {
    if (!bookingData?.id || !invoiceID) {
      setIsLoading(false);
      return;
    }

    const invoiceRef = doc(
      db,
      'Businesses',
      bid,
      'Bookings',
      bookingData.id,
      'Invoices',
      invoiceID
    );

    const unsubscribe = onSnapshot(
      invoiceRef,
      (snap) => {
        if (!snap.exists()) {
          setIsLoading(false);
          return;
        }

        const inv = { id: snap.id, ...snap.data() };
        setData(inv);

        // total paid = principal + tax + overdue received
        const principalReceived = safeNum(inv.principalReceived);
        const taxReceived = safeNum(inv.taxReceived);
        const overdueInterestReceived = safeNum(
          inv.overdueInterestReceived
        );
        const overdueTaxReceived = safeNum(
          inv.overdueTaxReceived
        );

        const paid =
          principalReceived +
          taxReceived +
          overdueInterestReceived +
          overdueTaxReceived;
        setTotalPaidAmount(paid);

        // overdue summary
        const overdueInterest = safeNum(inv.overdueInterest);
        const overdueTax = safeNum(inv.overdueTax);
        const overdueTotal = overdueInterest + overdueTax;
        const overdueReceived =
          overdueInterestReceived + overdueTaxReceived;
        const overdueOutstanding = overdueTotal - overdueReceived;

        setOverdueSummary({
          overdueTotal,
          overdueReceived,
          overdueOutstanding,
        });

        // receipts
        getReceipts(bookingData.id, invoiceID);

        setIsLoading(false);
      },
      (error) => {
        console.log(error.message);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emailHtml = render(
    <body>
      {/* Reserved for future email template */}
      <h1>HELLO!</h1>
    </body>
  );

  function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  async function sendMail(invoiceNumber, recipientAddress) {
    const fileName = `${invoiceNumber}.pdf`;
    const pdf = new jsPDF();
    pdf.text(`Invoice #${invoiceNumber}\nThank you!`, 10, 10);
    const pdfArrayBuffer = pdf.output('arraybuffer');
    const pdfBlob = new Blob([pdfArrayBuffer], {
      type: 'application/pdf',
    });
    const pdfBase64 = await blobToBase64(pdfBlob);

    if (pdfBase64) {
      try {
        const docRef = await addDoc(
          collection(db, 'Mails'),
          {
            to: [recipientAddress],
            message: {
              subject: `Invoice No. ${invoiceNumber}`,
              text: 'Thank you for your prompt payment.',
              html: `<div><p style="font-size: 24px; font-weight: bold;">Hello PartyName,</p><p>Please find your invoice attached.</p></div>`,
            },
            attachments: [
              {
                filename: fileName,
                content: pdfBase64,
                encoding: 'base64',
                contentType: 'application/pdf',
              },
            ],
          }
        );

        if (docRef.id) {
          setIsLoading(false);
          toast.success('Invoice Sent, via Email');
        }
      } catch (error) {
        console.log('Sending Invoice Email [Error] : ', error);
        setIsLoading(false);
      }
    }
  }

  const handlePrint = useReactToPrint({
    documentTitle: data && data.id,
    content: () => targetInvoiceRef.current,
    onBeforePrint: () => setIsProcessing(true),
    onAfterPrint: () => setIsProcessing(false),
  });

  return (
    <section className='section'>
      <OutletHead
        left={
          <div className='d-flex align-items-center'>
            <Button
              variant='light'
              size='sm'
              className='p-2 me-3 rounded-circle'
              onClick={() => navigate(-1)}
            >
              <IoArrowBackOutline size={24} />
            </Button>
            <h5 className='mb-0 fw-600'>{data && data.id}</h5>
          </div>
        }
        right={
          <div className='web-view d-flex align-items-center justify-content-end'>
            <Button
              variant='light'
              type='button'
              className='border py-2 px-4 me-3'
            >
              <IoSettingsOutline size={20} />
              <span className='ps-2'>Settings</span>
            </Button>
            <Button
              variant='outline-primary'
              type='button'
              className='py-2 px-4'
              size='sm'
            >
              <IoDocumentTextOutline size={18} />
              <span className='ps-2'>Export</span>
            </Button>
          </div>
        }
      />
      <div className='inner-wrapper border-top'>
        {isLoading ? (
          <div className='row px-3 pt-3'>
            <div className='col-md-9 col-12'>
              <div className='mb-3'>
                <Skeleton
                  height={80}
                  baseColor='var(--primary-light)'
                  highlightColor='linear-gradient(to right, #f7f8ff ,rgb(239, 239, 255))'
                />
              </div>
              <Skeleton
                height={'70vh'}
                baseColor='var(--primary-light)'
                highlightColor='linear-gradient(to right, #f7f8ff ,rgb(239, 239, 255))'
              />
            </div>
            <div className='col-md-3 col-12'>
              <div className='mb-3'>
                <Skeleton
                  height={80}
                  baseColor='var(--primary-light)'
                  highlightColor='linear-gradient(to right, #f7f8ff ,rgb(239, 239, 255))'
                />
              </div>
              <Skeleton
                height={'70vh'}
                baseColor='var(--primary-light)'
                highlightColor='linear-gradient(to right, #f7f8ff ,rgb(239, 239, 255))'
              />
            </div>
          </div>
        ) : (
          <div className='row'>
            {/* MAIN INVOICE */}
            <div className='col-md-9 bg-light'>
              <div className='d-flex align-items-center justify-content-center'>
                <div className='invoice-container'>
                  <div className='row sticky-top py-3 bg-light'>
                    <div className='col-8 d-flex justify-content-start'>
                      <OverlayTrigger
                        placement='bottom'
                        overlay={
                          <Tooltip>
                            <small>View</small>
                          </Tooltip>
                        }
                      >
                        <Button
                          variant='white icon-button p-0 me-2'
                          onClick={() =>
                            generatePDF(targetInvoiceRef, {
                              method: 'open',
                              filename: `${data.id}.pdf`,
                            })
                          }
                        >
                          <IoEye size={18} />
                        </Button>
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement='bottom'
                        overlay={
                          <Tooltip>
                            <small>Download</small>
                          </Tooltip>
                        }
                      >
                        <Button
                          variant='white icon-button p-0 me-2'
                          onClick={() =>
                            generatePDF(targetInvoiceRef, {
                              method: 'save',
                              filename: `${data.id}.pdf`,
                            })
                          }
                        >
                          <IoCloudDownload size={18} />
                        </Button>
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement='bottom'
                        overlay={
                          <Tooltip>
                            <small>Send</small>
                          </Tooltip>
                        }
                      >
                        <Button
                          variant='white icon-button p-0 me-2'
                          onClick={() => {
                            if (
                              bookingData?.partyDetails?.email
                            ) {
                              sendMail(
                                data.id,
                                bookingData.partyDetails.email
                              );
                            } else {
                              toast.error(
                                "Party's email doesn't exist!"
                              );
                            }
                          }}
                        >
                          <IoSend size={18} />
                        </Button>
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement='bottom'
                        overlay={
                          <Tooltip>
                            <small>Share</small>
                          </Tooltip>
                        }
                      >
                        <Button
                          variant='white icon-button p-0 me-2'
                          onClick={() => null}
                        >
                          <IoShareSocial size={18} />
                        </Button>
                      </OverlayTrigger>
                    </div>
                    <div className='col-4 d-flex justify-content-end'>
                      <Button
                        variant='primary'
                        size='sm'
                        className='px-3'
                        onClick={() => {
                          setIsProcessing(true);
                          handlePrint();
                        }}
                      >
                        {isProcessing ? (
                          <Spinner
                            as='span'
                            animation='border'
                            size='sm'
                            role='status'
                          />
                        ) : (
                          <>
                            <BsPrinter size={18} />
                            <span className='ms-2'>Print</span>
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  <div className='mt-5 bg-white'>
                    {data && (
                      <InvoiceTemplate
                        totalPaidAmount={totalPaidAmount}
                        targetInvoiceRef={targetInvoiceRef}
                        navigate={navigate}
                        state={bookingData}
                        invoiceDetails={data}
                        businessProfile={businessProfile}
                        overdueSummary={overdueSummary}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT SIDEBAR */}
            <div className='col-md-3 ps-0'>
              <div className='right-side-items'>
                <div className='sticky-top'>
                  {/* Payment summary */}
                  <div className='p-3 w-100 bg-primary-light'>
                    <h5 className='mb-0 text-muted fw-600'>
                      Payment Summary
                    </h5>
                  </div>
                  <div className='border-top p-3'>
                    <div className='d-flex align-items-center justify-content-between mb-1'>
                      <span className='text-muted small'>
                        Overdue Total
                      </span>
                      <span className='fw-600'>
                        {formatINR(overdueSummary.overdueTotal)}
                      </span>
                    </div>
                    <div className='d-flex align-items-center justify-content-between mb-1'>
                      <span className='text-muted small'>
                        Overdue Received
                      </span>
                      <span className='fw-600'>
                        {formatINR(
                          overdueSummary.overdueReceived
                        )}
                      </span>
                    </div>
                    <div className='d-flex align-items-center justify-content-between mb-1'>
                      <span className='text-muted small'>
                        Overdue Outstanding
                      </span>
                      <span className='fw-600 text-danger'>
                        {formatINR(
                          overdueSummary.overdueOutstanding
                        )}
                      </span>
                    </div>
                    <hr />
                    <div className='d-flex align-items-center justify-content-between mb-1'>
                      <span className='text-muted small'>
                        Total Paid
                      </span>
                      <span className='fw-600'>
                        {formatINR(totalPaidAmount)}
                      </span>
                    </div>
                  </div>

                  {/* Receipts list */}
                  <div className='p-3 w-100 bg-primary-light border-top'>
                    <h5 className='mb-0 text-muted fw-600'>
                      Payment Receipts
                    </h5>
                  </div>
                  <div className='border-top p-3'>
                    {receipts &&
                      receipts.map((rept, index) => (
                        <div
                          key={index}
                          className='border-bottom'
                        >
                          <Link
                            to={`/admin/receipts/${rept.id}`}
                            state={{ id: rept.id }}
                            className='d-flex align-items-center'
                          >
                            <IoReceiptOutline size={16} />
                            <span className='underline-anim ms-2'>
                              {rept.id}
                            </span>
                          </Link>
                          <p className='mb-0 mt-2 d-flex align-items-center justify-content-end'>
                            <span className='ps-1'>
                              {formatINR(
                                rept.amountReceived
                              )}
                            </span>
                          </p>
                          <p className='mb-3 fw-light fs-small text-end text-light-muted'>
                            <span>
                              <em>
                                Paid on -{' '}
                                {rept.date &&
                                  new Date(
                                    rept.date.seconds * 1000
                                  ).toDateString()}
                              </em>
                            </span>
                          </p>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default InvoiceDetails;